import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { stringToColor } from "../../components/CalendarCalendar/calendarHelpers";
import { EmptyState } from "../../components/ui";
import { useTenantRole } from "../../hooks/useTenantRole";
import {
  formatNoteAuthor,
  canDeleteNote,
  compactRelativeTime,
  isFeatureNotIncluded,
} from "./messageCenterHelpers";
import api from "../../services/api";
import styles from "./NotesPanel.module.css";

/**
 * Notas internas por conversa (F2.6).
 *
 * Painel de anotações que só a equipe vê — nunca vai para o cliente. Entra no
 * bloco de detalhes do `ConversationThread`, logo abaixo das tags (F2.5).
 *
 * Autocontido: busca as próprias notas em `GET /tenants/sessions/:id/notes`
 * (mais recente primeiro) ao abrir/trocar a conversa, cria via
 * `POST .../notes { body }` (anexa no topo) e exclui via
 * `DELETE /tenants/notes/:noteId`.
 *
 * Visibilidade do "excluir": OWNER/ADMIN podem apagar qualquer nota; um MEMBER
 * só a própria (`note.authorId === memberId`) — decidido por `canDeleteNote`
 * com o `role`/`memberId` do `useTenantRole`. O backend reforça a regra (403
 * `FORBIDDEN`), tratado aqui com toast amigável.
 *
 * Autor removido da equipe: `authorId === null` degrada para o nome do snapshot
 * com o sufixo "(removido)" (via `formatNoteAuthor`) — o registro sobrevive.
 */
export default function NotesPanel({ sessionId, onError, onFeatureBlocked }) {
  const { role, memberId } = useTenantRole();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  // Notas com DELETE em voo — desabilita o botão e evita duplo clique.
  const [deleting, setDeleting] = useState(() => new Set());

  // Busca as notas ao abrir/trocar a conversa. `cancelled` descarta a resposta
  // obsoleta quando o atendente já abriu outra conversa antes de ela chegar.
  useEffect(() => {
    if (!sessionId) {
      setNotes([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setNotes([]);
    (async () => {
      try {
        const res = await api.get(`/tenants/sessions/${sessionId}/notes`);
        if (cancelled) return;
        setNotes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (cancelled) return;
        if (isFeatureNotIncluded(err)) {
          onFeatureBlocked?.();
          return;
        }
        onError?.("Falha ao carregar as notas internas desta conversa.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, onError, onFeatureBlocked]);

  const markDeleting = (id, pending) => {
    setDeleting((prev) => {
      const next = new Set(prev);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleAdd = async () => {
    const body = draft.trim();
    if (!body || adding) return;
    setAdding(true);
    try {
      const res = await api.post(`/tenants/sessions/${sessionId}/notes`, {
        body,
      });
      // A resposta traz o snapshot (authorId/authorName/createdAt). Anexa no topo
      // — a lista é sempre "mais recente primeiro".
      setNotes((prev) => [res.data, ...prev]);
      setDraft("");
    } catch (err) {
      if (isFeatureNotIncluded(err)) {
        onFeatureBlocked?.();
        return;
      }
      const msg =
        err?.response?.data?.error || "Falha ao adicionar a nota. Tente novamente.";
      onError?.(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl/Cmd+Enter envia — o Enter simples quebra linha (notas podem ser
    // multilinha), espelhando o composer de mensagens.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = async (noteId) => {
    if (deleting.has(noteId)) return;
    markDeleting(noteId, true);
    try {
      await api.delete(`/tenants/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      if (isFeatureNotIncluded(err)) {
        onFeatureBlocked?.();
        return;
      }
      const code = err?.response?.data?.code;
      const msg =
        code === "FORBIDDEN"
          ? "Você só pode excluir as suas próprias notas."
          : err?.response?.data?.error || "Falha ao excluir a nota.";
      onError?.(msg);
    } finally {
      markDeleting(noteId, false);
    }
  };

  return (
    <section className={styles.wrap} aria-labelledby="notes-panel-label">
      <div className={styles.head}>
        <span className={styles.label} id="notes-panel-label">
          Notas internas
        </span>
        <span className={styles.hint}>só a equipe vê</span>
      </div>

      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          handleAdd();
        }}
      >
        <textarea
          className={styles.textarea}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          placeholder="Escreva uma nota interna… (Ctrl+Enter para adicionar)"
          disabled={adding}
          aria-label="Nova nota interna"
        />
        <button
          type="submit"
          className={styles.addBtn}
          disabled={adding || !draft.trim()}
          aria-busy={adding}
        >
          {adding ? "Adicionando…" : "Adicionar nota"}
        </button>
      </form>

      {loading ? (
        <p className={styles.empty}>Carregando notas…</p>
      ) : notes.length === 0 ? (
        <EmptyState
          size="sm"
          icon="🗒️"
          description="Nenhuma nota interna ainda."
        />
      ) : (
        <ul className={styles.list} aria-label="Notas internas da conversa">
          {notes.map((note) => {
            // Nome "cru" do snapshot (sem o sufixo de removido) — base estável
            // para cor e iniciais do avatar; o rótulo visível usa formatNoteAuthor.
            const name = String(note.authorName ?? "").trim() || "Atendente";
            const authorLabel = formatNoteAuthor(note);
            const removed = note.authorId === null;
            const timeLabel = compactRelativeTime(note.createdAt);
            const canDelete = canDeleteNote(note, { role, memberId });
            const isDeleting = deleting.has(note.id);
            return (
              <li key={note.id} className={styles.note}>
                <span
                  className={styles.avatar}
                  style={{ background: stringToColor(name) }}
                  aria-hidden="true"
                >
                  {initials(name)}
                </span>
                <div className={styles.body}>
                  <div className={styles.meta}>
                    <span
                      className={`${styles.author} ${removed ? styles.authorRemoved : ""}`}
                    >
                      {authorLabel}
                    </span>
                    {timeLabel && (
                      <span className={styles.time}>· {timeLabel}</span>
                    )}
                  </div>
                  <p className={styles.text}>{note.body}</p>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    className={styles.delete}
                    onClick={() => handleDelete(note.id)}
                    disabled={isDeleting}
                    aria-busy={isDeleting}
                    aria-label={`Excluir nota de ${authorLabel}`}
                    title="Excluir nota"
                  >
                    🗑
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Iniciais (até 2) para o avatar da nota, a partir do rótulo do autor. */
function initials(name) {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

NotesPanel.propTypes = {
  sessionId: PropTypes.string.isRequired,
  onError: PropTypes.func,
  onFeatureBlocked: PropTypes.func,
};
